/**
 * EditControlsOverlay Component
 *
 * Floating overlay with editing controls for Kanban columns in Create Board form.
 * Extracted from ColumnBuilderRow to separate editing UI from display UI.
 * Allows EditableKanbanColumn to reuse the original KanbanColumn component.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Icon, IconName } from '@/icons';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { shadows, sizing, radius } from '@/styles/design-tokens';
import { CreateBoardColumnsItem } from '@/types/generated/processor-types';
import {
  ConfirmOnExitConfig,
  ConfirmOnEntryConfig,
  CONFIRMABLE_FIELD_OPTIONS,
} from '@/types/board-config.types';
import { UserRole } from '@/types/enums/UserRole';

const BACKWARD_ALLOWED_ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.MENTOR, label: 'Mentors' },
  { value: UserRole.ADMIN, label: 'Admins' },
];

interface EditControlsOverlayProps {
  column: CreateBoardColumnsItem;
  index: number;
  totalColumns: number;
  onUpdate: (index: number, column: CreateBoardColumnsItem) => void;
  onRemove: (index: number) => void;
}

import { getPresetColumnColors } from '@/business/constants/column-colors';

export const EditControlsOverlay: React.FC<EditControlsOverlayProps> = ({
  column,
  index,
  totalColumns,
  onUpdate,
  onRemove,
}) => {
  const colors = useThemeColors();
  const [isEditingName, setIsEditingName] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const rulesRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const maxCards = (column.config?.maxCards as number) || undefined;
  const forwardOnly = (column.config?.forwardOnly as boolean) || false;
  const backwardAllowedRoles =
    (column.config?.backwardAllowedRoles as UserRole[] | undefined) || [];
  const lockEdits = (column.config?.lockEdits as boolean) || false;
  const confirmOnExit =
    (column.config?.confirmOnExit as ConfirmOnExitConfig | undefined) ||
    undefined;
  const confirmOnEntry =
    (column.config?.confirmOnEntry as ConfirmOnEntryConfig | undefined) ||
    undefined;
  const activeRulesCount =
    (maxCards !== undefined ? 1 : 0) +
    (forwardOnly ? 1 : 0) +
    (lockEdits ? 1 : 0) +
    (confirmOnExit ? 1 : 0) +
    (confirmOnEntry ? 1 : 0);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorPicker]);

  // Close rules panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rulesRef.current &&
        !rulesRef.current.contains(event.target as Node)
      ) {
        setShowRules(false);
      }
    };

    if (showRules) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showRules]);

  // Focus input when editing name
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleConfigChange = (configUpdate: Record<string, unknown>) => {
    onUpdate(index, {
      ...column,
      config: { ...(column.config || {}), ...configUpdate },
    });
  };

  const handleNameChange = (newName: string) => {
    onUpdate(index, { ...column, name: newName });
  };

  return (
    <div
      className="absolute top-1.5 right-10 z-10 flex flex-row items-center gap-1"
      onClick={e => e.stopPropagation()} // Prevent clicks from bubbling to column
    >
      {/* Edit Name Button */}
      {!isEditingName && (
        <button
          type="button"
          onClick={() => setIsEditingName(true)}
          className="p-1.5 rounded transition-colors opacity-80 hover:opacity-100"
          style={{
            backgroundColor: colors.background,
            color: colors.text.secondary,
            boxShadow: shadows.soft,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = colors.hover.background;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = colors.background;
          }}
          title="Edit column name"
        >
          <Icon name={IconName.EDIT} size="sm" />
        </button>
      )}

      {/* Name Input (when editing) */}
      {isEditingName && (
        <input
          ref={nameInputRef}
          type="text"
          value={column.name}
          onChange={e => handleNameChange(e.target.value)}
          onBlur={() => setIsEditingName(false)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              setIsEditingName(false);
            }
          }}
          maxLength={50}
          className="px-2 py-1 text-sm font-semibold rounded"
          style={{
            backgroundColor: colors.background,
            color: colors.text.primary,
            border: `1px solid ${colors.border.default}`,
            boxShadow: shadows.soft,
            minWidth: 'calc(120px * var(--sizing-multiplier, 1))',
          }}
          onClick={e => e.stopPropagation()}
        />
      )}

      {/* Color Picker Button */}
      <div className="relative" ref={colorPickerRef}>
        <button
          type="button"
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="p-1.5 rounded transition-colors opacity-80 hover:opacity-100 flex items-center justify-center"
          style={{
            backgroundColor: colors.background,
            boxShadow: shadows.soft,
          }}
          title="Change color"
        >
          <div
            className="w-4 h-4 rounded-full border"
            style={{
              backgroundColor: column.color,
              borderColor: colors.border.default,
            }}
          />
        </button>

        {/* Color Picker Dropdown */}
        {showColorPicker && (
          <div
            className="absolute top-full right-0 mt-2 p-3 rounded-lg shadow-xl z-50"
            style={{
              backgroundColor: colors.background,
              border: `1px solid ${colors.border.default}`,
              minWidth: 'calc(200px * var(--sizing-multiplier, 1))',
            }}
          >
            <div
              className="text-xs font-medium mb-2"
              style={{ color: colors.text.secondary }}
            >
              Choose Color
            </div>
            <div className="grid grid-cols-4 gap-2">
              {getPresetColumnColors(colors).map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    onUpdate(index, { ...column, color: value });
                    setShowColorPicker(false);
                  }}
                  className="w-10 h-10 rounded-lg hover:scale-105 transition-transform flex items-center justify-center"
                  style={{
                    backgroundColor: value,
                    border:
                      value === column.color
                        ? `2px solid ${colors.text.primary}`
                        : `1px solid ${colors.border.default}`,
                    boxShadow:
                      value === column.color
                        ? `0 0 0 2px ${withOpacity(colors.text.primary, opacity.soft)}`
                        : 'none',
                  }}
                  title={label}
                >
                  {value === column.color && (
                    <Icon
                      name={IconName.CHECK}
                      size="sm"
                      style={{ color: colors.background }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rules Button */}
      <div ref={rulesRef} className="relative">
        <button
          type="button"
          onClick={() => setShowRules(!showRules)}
          className="p-1.5 rounded transition-colors opacity-80 hover:opacity-100 relative"
          style={{
            backgroundColor: colors.background,
            color: colors.text.secondary,
            boxShadow: shadows.soft,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = colors.hover.background;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = colors.background;
          }}
          title="Configure column rules and constraints"
        >
          <Icon name={IconName.SETTINGS} size="sm" />
          {activeRulesCount > 0 && (
            <span
              className="absolute -top-1 -right-1 w-4 h-4 text-xs flex items-center justify-center rounded-full font-semibold"
              style={{
                backgroundColor: colors.primary,
                color: colors.background,
              }}
            >
              {activeRulesCount}
            </span>
          )}
        </button>

        {/* Rules Panel Dropdown */}
        {showRules && (
          <div
            className="absolute top-full right-0 mt-2 rounded-lg z-50"
            style={{
              backgroundColor: colors.offWhite,
              border: `1px solid ${colors.border.default}`,
              width: 'calc(260px * var(--sizing-multiplier, 1))',
              boxShadow: shadows['soft-md'],
            }}
          >
            <div className="p-3 space-y-3">
              {/* Max cards rule */}
              <div>
                <label
                  className="flex items-start gap-2 cursor-pointer rounded-md p-2 -m-2 transition-colors"
                  style={{
                    color: colors.text.primary,
                    minHeight: sizing.button.xl,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      colors.hover.background;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={maxCards !== undefined}
                    onChange={e => {
                      if (e.target.checked) {
                        handleConfigChange({ maxCards: 5 });
                      } else {
                        const newConfig = { ...(column.config || {}) };
                        delete newConfig.maxCards;
                        onUpdate(index, { ...column, config: newConfig });
                      }
                    }}
                    className="mt-0.5"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Limit cards</div>
                    <div
                      className="text-xs"
                      style={{ color: colors.text.tertiary }}
                    >
                      Caps how many cards can sit in this column
                    </div>
                  </div>
                </label>
                {maxCards !== undefined && (
                  <div className="mt-2 pl-6">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={maxCards}
                      onChange={e =>
                        handleConfigChange({
                          maxCards: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full px-3 py-2 rounded-md border text-sm"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border.default,
                        color: colors.text.primary,
                      }}
                      placeholder="e.g. 5"
                    />
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  height: '1px',
                  backgroundColor: colors.border.default,
                }}
              />

              {/* Forward-only rule */}
              <div>
                <label
                  className="flex items-start gap-2 cursor-pointer rounded-md p-2 -m-2 transition-colors"
                  style={{
                    color: colors.text.primary,
                    minHeight: sizing.button.xl,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      colors.hover.background;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={forwardOnly}
                    onChange={e => {
                      if (e.target.checked) {
                        handleConfigChange({ forwardOnly: true });
                      } else {
                        const newConfig = { ...(column.config || {}) };
                        delete newConfig.forwardOnly;
                        delete newConfig.backwardAllowedRoles;
                        onUpdate(index, { ...column, config: newConfig });
                      }
                    }}
                    className="mt-0.5"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Forward-only</div>
                    <div
                      className="text-xs"
                      style={{ color: colors.text.tertiary }}
                    >
                      Cards can only move out of here to a later column —
                      blocked for everyone by default
                    </div>
                  </div>
                </label>
                {forwardOnly && (
                  <div className="mt-2 pl-6">
                    <div
                      className="text-xs font-medium mb-1"
                      style={{ color: colors.text.secondary }}
                    >
                      Allow backward move for:
                    </div>
                    <div className="flex flex-col gap-1">
                      {BACKWARD_ALLOWED_ROLE_OPTIONS.map(opt => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 cursor-pointer"
                          style={{ color: colors.text.primary }}
                        >
                          <input
                            type="checkbox"
                            checked={backwardAllowedRoles.includes(opt.value)}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...backwardAllowedRoles, opt.value]
                                : backwardAllowedRoles.filter(
                                    r => r !== opt.value
                                  );
                              handleConfigChange({
                                backwardAllowedRoles: next,
                              });
                            }}
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                          />
                          <span className="text-xs">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  height: '1px',
                  backgroundColor: colors.border.default,
                }}
              />

              {/* Confirm on exit rule */}
              <div>
                <label
                  className="flex items-start gap-2 cursor-pointer rounded-md p-2 -m-2 transition-colors"
                  style={{
                    color: colors.text.primary,
                    minHeight: sizing.button.xl,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      colors.hover.background;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={confirmOnExit !== undefined}
                    onChange={e => {
                      if (e.target.checked) {
                        handleConfigChange({
                          confirmOnExit: {
                            field: CONFIRMABLE_FIELD_OPTIONS[0].value,
                          },
                        });
                      } else {
                        const newConfig = { ...(column.config || {}) };
                        delete newConfig.confirmOnExit;
                        onUpdate(index, { ...column, config: newConfig });
                      }
                    }}
                    className="mt-0.5"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Confirm on exit</div>
                    <div
                      className="text-xs"
                      style={{ color: colors.text.tertiary }}
                    >
                      Ask user to fill a field before card can leave this column
                    </div>
                  </div>
                </label>
                {confirmOnExit !== undefined && (
                  <div className="mt-2 pl-6">
                    <select
                      value={confirmOnExit.field}
                      onChange={e =>
                        handleConfigChange({
                          confirmOnExit: {
                            ...confirmOnExit,
                            field: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 rounded-md border text-sm"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border.default,
                        color: colors.text.primary,
                      }}
                    >
                      {CONFIRMABLE_FIELD_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  height: '1px',
                  backgroundColor: colors.border.default,
                }}
              />

              {/* Confirm on entry rule */}
              <div>
                <label
                  className="flex items-start gap-2 cursor-pointer rounded-md p-2 -m-2 transition-colors"
                  style={{
                    color: colors.text.primary,
                    minHeight: sizing.button.xl,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      colors.hover.background;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={confirmOnEntry !== undefined}
                    onChange={e => {
                      if (e.target.checked) {
                        handleConfigChange({
                          confirmOnEntry: {
                            field: CONFIRMABLE_FIELD_OPTIONS[0].value,
                          },
                        });
                      } else {
                        const newConfig = { ...(column.config || {}) };
                        delete newConfig.confirmOnEntry;
                        onUpdate(index, { ...column, config: newConfig });
                      }
                    }}
                    className="mt-0.5"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">Confirm on entry</div>
                    <div
                      className="text-xs"
                      style={{ color: colors.text.tertiary }}
                    >
                      Ask user to fill a field before card can enter this column
                    </div>
                  </div>
                </label>
                {confirmOnEntry !== undefined && (
                  <div className="mt-2 pl-6 flex flex-col gap-2">
                    <select
                      value={confirmOnEntry.field}
                      onChange={e =>
                        handleConfigChange({
                          confirmOnEntry: {
                            ...confirmOnEntry,
                            field: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 rounded-md border text-sm"
                      style={{
                        backgroundColor: colors.background,
                        borderColor: colors.border.default,
                        color: colors.text.primary,
                      }}
                    >
                      {CONFIRMABLE_FIELD_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    {/* Reflection sub-toggle */}
                    <label
                      className="flex items-start gap-2 cursor-pointer rounded-md px-2 py-1.5 transition-colors"
                      style={{
                        color: colors.text.primary,
                        backgroundColor: confirmOnEntry.requireReflection
                          ? `${colors.primary}18`
                          : 'transparent',
                        border: `1px solid ${confirmOnEntry.requireReflection ? colors.primary : colors.border.default}`,
                        borderRadius: radius.sm,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={confirmOnEntry.requireReflection === true}
                        onChange={e =>
                          handleConfigChange({
                            confirmOnEntry: {
                              ...confirmOnEntry,
                              requireReflection: e.target.checked || undefined,
                            },
                          })
                        }
                        className="mt-0.5"
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium">
                          Ask for reflection
                        </div>
                        <div
                          className="text-xs"
                          style={{ color: colors.text.tertiary }}
                        >
                          Understood · Still thinking · I need help
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  height: '1px',
                  backgroundColor: colors.border.default,
                }}
              />

              {/* Lock edits rule */}
              <label
                className="flex items-start gap-2 cursor-pointer rounded-md p-2 -m-2 transition-colors"
                style={{
                  color: colors.text.primary,
                  minHeight: sizing.button.xl,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor =
                    colors.hover.background;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={lockEdits}
                  onChange={e =>
                    handleConfigChange({ lockEdits: e.target.checked })
                  }
                  className="mt-0.5"
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Lock edits</div>
                  <div
                    className="text-xs"
                    style={{ color: colors.text.tertiary }}
                  >
                    Students can only update pomodoro counts and reflection in
                    this column — admins bypass
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Delete Button */}
      <button
        type="button"
        onClick={() => onRemove(index)}
        disabled={totalColumns === 1}
        className="p-1.5 rounded transition-colors opacity-80 hover:opacity-100"
        style={{
          backgroundColor: colors.background,
          color: totalColumns === 1 ? colors.disabled.text : colors.error,
          cursor: totalColumns === 1 ? 'not-allowed' : 'pointer',
          boxShadow: shadows.soft,
        }}
        onMouseEnter={e => {
          if (totalColumns > 1) {
            e.currentTarget.style.backgroundColor = colors.hover.background;
          }
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = colors.background;
        }}
        title={
          totalColumns === 1 ? 'Cannot remove last column' : 'Remove column'
        }
      >
        <Icon name={IconName.X} size="sm" />
      </button>
    </div>
  );
};
